import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import { SearchableSelect } from '../../../components/ui';

const UniversityApplicationCreatePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const caseId = searchParams.get('case_id') || '';
  const clientId = searchParams.get('client_id') || '';

  const [form, setForm] = useState({
    university: '',
    program: '',
    country: '',
    intake_id: '',
    portal_ref: '',
    submitted_at: '',
    fees_json: '',
    application_fee: '',
    deadline: '',
  });
  const [files, setFiles] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [selectedUniversityId, setSelectedUniversityId] = useState('');
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [selectedIntakeId, setSelectedIntakeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [warnMissingDeadline, setWarnMissingDeadline] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    (async () => {
      try {
        const { data: unis } = await supabase.from('universities').select('id,name,country').order('name');
        setUniversities(unis || []);
      } catch { }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!selectedUniversityId) { setPrograms([]); setIntakes([]); setSelectedProgramId(''); setSelectedIntakeId(''); return; }
      const { data: progs } = await supabase.from('programs').select('id,name,level,discipline').eq('university_id', selectedUniversityId).order('name');
      setPrograms(progs || []);
    })();
  }, [selectedUniversityId]);

  useEffect(() => {
    (async () => {
      if (!selectedProgramId) { setIntakes([]); setSelectedIntakeId(''); return; }
      const { data: ints } = await supabase
        .from('intakes')
        .select('id,term,apply_close,year,start_date')
        .eq('program_id', selectedProgramId)
        .order('apply_close');
      setIntakes(ints || []);
    })();
  }, [selectedProgramId]);

  const createApplication = async () => {
    try {
      const uniName = form.university.trim() || (universities.find(u => u.id === selectedUniversityId)?.name || '');
      const programName = form.program.trim() || (programs.find(p => p.id === selectedProgramId)?.name || '');
      if (!uniName && !programName) {
        setToast({ type: 'error', message: 'Provide university or program' });
        return;
      }
      if (!form.deadline && !intakes.find(i => i.id === selectedIntakeId)?.apply_close) {
        setWarnMissingDeadline(true);
      } else {
        setWarnMissingDeadline(false);
      }
      setSaving(true);
      const payload = {
        case_id: caseId || null,
        client_id: clientId || null,
        program_id: selectedProgramId || null,
        intake_id: selectedIntakeId || null,
        status: form.submitted_at ? 'submitted' : 'draft',
        submitted_at: form.submitted_at || null,
        metadata: {
          university: uniName || null,
          program_text: programName || null,
          country: form.country || (universities.find(u => u.id === selectedUniversityId)?.country || null),
          portal_ref: form.portal_ref || null,
          fees: form.fees_json ? JSON.parse(form.fees_json) : {},
          application_fee: form.application_fee ? Number(form.application_fee) : null,
          deadline: form.deadline || null,
        }
      };
      const { data: inserted, error } = await supabase
        .from('uni_applications')
        .insert([payload])
        .select('*')
        .single();
      if (error) throw error;
      const appId = inserted?.id;
      // Generate checklist from program_requirements
      try {
        if (selectedProgramId) {
          const { data: reqs } = await supabase.from('program_requirements').select('requirement_key,label,mandatory').eq('program_id', selectedProgramId);
          const docs = (reqs || []).map(r => ({ uni_application_id: appId, template_id: null, status: r.mandatory ? 'requested' : 'optional', notes: r.label }));
          if (docs.length) await supabase.from('uni_app_documents').insert(docs);
        }
      } catch { }
      // Upload attachments
      for (const file of files) {
        const path = `cases/${caseId}/univ_apps/${appId}/${Date.now()}_${file.name}`;
        const { error: upErr } = await supabase.storage.from('documents').upload(path, file, { upsert: true });
        if (!upErr) {
          const { data: pub } = await supabase.storage.from('documents').getPublicUrl(path);
          try {
            await supabase.from('case_notes').insert([{ case_id: caseId, content: `Univ app ${appId} attachment: ${pub?.publicUrl || path} (${file.name})` }]);
          } catch { /* non-blocking */ }
        }
      }
      try {
        await supabase.from('case_notes').insert([{ case_id: caseId, content: `University application created: ${payload.university || ''} • ${payload.program || ''}` }]);
      } catch { /* non-blocking */ }
      setToast({ type: 'success', message: 'Application created' });
      // Navigate back to case or to applications list
      if (caseId) navigate(`/admin/cases/${caseId}`);
      else navigate('/admin');
    } catch (e) {
      setToast({ type: 'error', message: e.message || 'Failed to create application' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded shadow-lg border ${toast.type === 'success' ? 'bg-green-900/90 border-green-700 text-green-100' : 'bg-red-900/90 border-red-700 text-red-100'
          }`}>
          {toast.message}
        </div>
      )}

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">New University Application</h1>
            <p className="text-gray-400 text-sm">Create a new application for a student</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Application Details */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="font-bold text-white text-sm uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Program Selection
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">University *</label>
                    <SearchableSelect
                      options={universities.map(u => ({ value: u.id, label: u.name }))}
                      value={selectedUniversityId}
                      onChange={val => {
                        setSelectedUniversityId(val);
                        setSelectedProgramId('');
                        setSelectedIntakeId('');
                        const selected = (universities || []).find(u => u.id === val);
                        setForm(f => ({ ...f, university: selected?.name || '', country: selected?.country || f.country }));
                      }}
                      placeholder="Search University..."
                      className="w-full"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Program *</label>
                    <select
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      value={selectedProgramId}
                      onChange={e => {
                        const val = e.target.value;
                        setSelectedProgramId(val);
                        setSelectedIntakeId('');
                        const selected = (programs || []).find(p => p.id === val);
                        setForm(f => ({ ...f, program: selected?.name || '' }));
                      }}
                      disabled={!selectedUniversityId}
                    >
                      <option value="">Select Program</option>
                      {programs.map(p => <option key={p.id} value={p.id}>{p.name} ({p.level})</option>)}
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Intake *</label>
                    <select
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      value={selectedIntakeId}
                      onChange={e => setSelectedIntakeId(e.target.value)}
                      disabled={!selectedProgramId}
                    >
                      <option value="">Select Intake</option>
                      {intakes.map(i => (
                        <option key={i.id} value={i.id}>
                          {i.term} {i.year ? `(${i.year})` : ''} {i.start_date ? `- Start: ${i.start_date}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Student Info */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="font-bold text-white text-sm uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Application Details
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Portal Ref</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="e.g. APP-12345"
                      value={form.portal_ref}
                      onChange={e => setForm({ ...form, portal_ref: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Application Fee</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="0.00"
                      value={form.application_fee}
                      onChange={e => setForm({ ...form, application_fee: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Submitted At</label>
                    <input
                      type="date"
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      value={form.submitted_at}
                      onChange={e => setForm({ ...form, submitted_at: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Deadline</label>
                    <input
                      type="date"
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      value={form.deadline}
                      onChange={e => setForm({ ...form, deadline: e.target.value })}
                    />
                    {warnMissingDeadline && !form.deadline && !intakes.find(i => i.id === selectedIntakeId)?.apply_close && (
                      <div className="mt-2 text-xs text-yellow-400">
                        No deadline provided (custom or intake); this application will not trigger Control Room alerts.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Attachments */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="font-bold text-white text-sm uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  Documents
                </h3>
              </div>
              <div className="p-6">
                <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center hover:border-gray-500 transition-colors bg-gray-900/50">
                  <input
                    type="file"
                    multiple
                    onChange={e => setFiles(Array.from(e.target.files || []))}
                    className="hidden"
                    id="app-files"
                  />
                  <label htmlFor="app-files" className="cursor-pointer flex flex-col items-center justify-center">
                    <svg className="w-12 h-12 text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-base text-gray-300 font-medium mb-1">Click to upload documents</span>
                    <span className="text-xs text-gray-500">PDF, JPG, PNG up to 10MB</span>
                  </label>
                </div>

                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-900 border border-gray-700 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-gray-200 font-medium">{f.name}</span>
                        </div>
                        <span className="text-xs text-gray-500">{(f.size / 1024).toFixed(1)} KB</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Summary & Actions */}
          <div className="space-y-6">
            <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
              <div className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">Summary</h3>

                <div className="space-y-4 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-400">University</span>
                    <span className="text-white font-medium text-right">{universities.find(u => u.id === selectedUniversityId)?.name || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-400">Program</span>
                    <span className="text-white font-medium text-right">{programs.find(p => p.id === selectedProgramId)?.name || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-400">Intake</span>
                    <span className="text-white font-medium text-right">{intakes.find(i => i.id === selectedIntakeId)?.term || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-400">Application Fee</span>
                    <span className="text-white font-medium text-right">
                      {form.application_fee ? `${form.application_fee}` : 'Free'}
                    </span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Status</label>
                    <span className="px-2 py-1 rounded text-xs font-bold bg-yellow-900/30 text-yellow-400 border border-yellow-800 uppercase">
                      {form.submitted_at ? 'SUBMITTED' : 'DRAFT'}
                    </span>
                  </div>
                </div>

                <button
                  onClick={createApplication}
                  disabled={saving}
                  className="w-full mt-6 px-6 py-3 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-900/20 transition-all transform active:scale-95 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Application'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversityApplicationCreatePage;
